import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';

// Selector de "Generate": en vez de ir directo al chat de Novah, ofrece las
// cuatro cosas que se pueden crear en el workspace (post / estilo / template /
// mejorar el brand). Cada opción rutea a la pantalla que ya existe.
// Se renderiza por portal a document.body para no depender del stacking del
// disparador (el sidebar usa transform en mobile → rompería position:fixed).

interface GenerateOption {
  icon: string;
  title: string;
  desc: string;
  to: (workspaceId: string) => string;
}

const OPTIONS: GenerateOption[] = [
  {
    icon: '✨',
    title: 'New post',
    desc: 'Chat with Novah to generate ready-to-post content.',
    to: (w) => `/workspace/${w}/generate`,
  },
  {
    icon: '🎨',
    title: 'Visual style',
    desc: 'Create an image style (from a photo or by hand) for your posts.',
    to: (w) => `/workspace/${w}/styles`,
  },
  {
    icon: '🖼️',
    title: 'Template',
    desc: 'Design a reusable template in the editor.',
    to: (w) => `/workspace/${w}/templates`,
  },
  {
    icon: '⭐',
    title: 'Improve brand',
    desc: 'Add sources and enrich your brand info with AI.',
    to: (w) => `/workspace/${w}/brand?tab=sources`,
  },
];

interface GenerateOptionsModalProps {
  workspaceId: string;
  open: boolean;
  onClose: () => void;
}

export function GenerateOptionsModal({ workspaceId, open, onClose }: GenerateOptionsModalProps) {
  const navigate = useNavigate();

  // Cerrar con Escape mientras está abierto.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const pick = (opt: GenerateOption) => {
    onClose();
    navigate(opt.to(workspaceId));
  };

  return createPortal(
    <Overlay onClick={onClose} role="dialog" aria-modal="true" aria-label="What to create">
      <Panel onClick={(e) => e.stopPropagation()}>
        <PanelHeader>
          <div>
            <PanelTitle>What do you want to create?</PanelTitle>
            <PanelSub>Pick an option to get started.</PanelSub>
          </div>
          <CloseBtn type="button" onClick={onClose} aria-label="Close">×</CloseBtn>
        </PanelHeader>

        <OptionsGrid>
          {OPTIONS.map((opt) => (
            <OptionCard key={opt.title} type="button" onClick={() => pick(opt)}>
              <OptionIcon aria-hidden>{opt.icon}</OptionIcon>
              <OptionTitle>{opt.title}</OptionTitle>
              <OptionDesc>{opt.desc}</OptionDesc>
            </OptionCard>
          ))}
        </OptionsGrid>
      </Panel>
    </Overlay>,
    document.body,
  );
}

/* ── Styles ── */

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;
const popIn = keyframes`
  from { opacity: 0; transform: translateY(12px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(8, 11, 22, 0.55);
  backdrop-filter: blur(3px);
  animation: ${fadeIn} 0.15s ease-out;
`;

const Panel = styled.div`
  width: 100%;
  max-width: 640px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.35);
  padding: ${({ theme }) => theme.spacing.lg};
  animation: ${popIn} 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const PanelTitle = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
`;

const PanelSub = styled.p`
  margin: 4px 0 0;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const CloseBtn = styled.button`
  flex: none;
  width: 32px;
  height: 32px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  border: none;
  background: ${({ theme }) => theme.colors.surfaceHover};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover { background: ${({ theme }) => theme.colors.border}; color: ${({ theme }) => theme.colors.text}; }
`;

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  @media (max-width: 560px) { grid-template-columns: 1fr; }
`;

const OptionCard = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  text-align: left;
  padding: 16px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
  cursor: pointer;
  transition: all 0.18s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 6px 18px rgba(59, 130, 246, 0.14);
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`;

const OptionIcon = styled.span`
  font-size: 26px;
  line-height: 1;
`;

const OptionTitle = styled.span`
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

const OptionDesc = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.4;
`;
